import React from "react";
import { FcLineChart } from 'react-icons/fc';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import { Pagination, Navigation } from 'swiper/modules';

const piIndexes = [
    {
        title: "USA Large Cap Growth & Income",
        description: "This strategy provides general exposure to US large capitalisation stocks by balancing growth and income goals powered by our proprietary PDE (Phase Detection Engine) model.",
        goLiveDate: "June 15, 2024",
        geography: "North America",
        i1_link: "https://indexone.io/index/48380df4-f142-4594-9f05-5a6ef4e1f8de-0/overview",
        returns: [
            { label: "2022", value: "6%" },
            { label: "2023", value: "24%" },
            { label: "2024", value: "28%" },
            { label: "2025", value: "16%" },
            { label: "YTD", value: "7%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "20" },
            { label: "DIVIDEND YIELD", value: "3.66%" },
            { label: "NYSE", value: "SWK (US)" },
            { label: "NYSE", value: "BEN (US)" },
            { label: "NYSE", value: "CVX (US)" }
        ]
    },
    {
        title: "All Country World xUS Large Cap Growth",
        description: "This strategy provides exposure to Global large capitalisation growth stocks outside the US powered by our proprietary PDE (Phase Detection Engine) model.",
        goLiveDate: "June 17, 2024",
        geography: "Global",
        i1_link: "https://indexone.io/index/a72bcfab-7335-4d07-8d96-f76c109cac6d-0/overview",
        returns: [
            { label: "2022", value: "-17%" },
            { label: "2023", value: "33%" },
            { label: "2024", value: "20%" },
            { label: "2025", value: "26%" },
            { label: "YTD", value: "5%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "20" },
            { label: "DIVIDEND YIELD", value: "2.65%" },
            { label: "NASDAQ", value: "TIGO (LU)" },
            { label: "NASDAQ", value: "CIB (CO)" },
            { label: "NASDAQ", value: "ROIV (GB)" }
        ]
    },
    {
        title: "Developed Europe Large Cap Growth & Income",
        description: "This strategy provides general exposure to Developed Europe large capitalisation stocks by balancing growth and income goals powered by our proprietary PDE (Phase Detection Engine) model.",
        goLiveDate: "August 08, 2024",
        geography: "North America",
        i1_link: "https://indexone.io/index/e46707ed-836f-4f96-919b-b22f6cdbf760-0/overview",
        returns: [
            { label: "2022", value: "-4%" },
            { label: "2023", value: "17%" },
            { label: "2024", value: "14%" },
            { label: "2025", value: "29%" },
            { label: "YTD", value: "5%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "20" },
            { label: "DIVIDEND YIELD", value: "2.03%" },
            { label: "XETRA", value: "HEI (DE)" },
            { label: "MCE", value: "IBE (ES)" },
            { label: "ENXT PA", value: "ENGI (FR)" }
        ]
    },
    {
        title: "Bitcoin",
        description: "This strategy provides managed exposure to Bitcoin powered by our proprietary PDE model by allocating between Bitcoin & US Treasuries.",
        goLiveDate: "January 15, 2026",
        geography: "Crypto",
        i1_link: "https://indexone.io/index/05a7df8e-493e-43b8-a02b-06397edd02bf-0/overview",
        returns: [
            { label: "2022", value: "-34%" },
            { label: "2023", value: "105%" },
            { label: "2024", value: "90%" },
            { label: "2025", value: "-12%" },
            { label: "YTD", value: "-6%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "DAILY" },
            { label: "HOLDINGS", value: "2" },
            { label: "DIVIDEND YIELD", value: "n/a" },
            { label: "NASDAQ", value: "IBIT (US)" },
            { label: "NASDAQ", value: "SHV (US)" }
        ]
    },
    {
        title: "Gold",
        description: "This strategy provides managed exposure to Gold powered by our proprietary PDE (Phase Detection Engine) model by allocating between Gold & US Treasuries.",
        goLiveDate: "January 15, 2026",
        geography: "Commodities",
        i1_link: "https://indexone.io/index/75e2304f-63ea-4579-8e66-59b16acaf360-0/overview",
        returns: [
            { label: "2022", value: "-3%" },
            { label: "2023", value: "-5%" },
            { label: "2024", value: "26%" },
            { label: "2025", value: "57%" },
            { label: "YTD", value: "17%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "DAILY" },
            { label: "HOLDINGS", value: "2" },
            { label: "DIVIDEND YIELD", value: "n/a%" },
            { label: "NASDAQ", value: "GLD (US)" },
            { label: "NYSE ARCA", value: "SHV (US)" }
        ]
    },
    {
        title: "Ethereum",
        description: "This strategy provides managed exposure to Ethereum powered by our proprietary PDE model by allocating between Ethereum & US Treasuries.",
        goLiveDate: "January 15, 2026",
        geography: "North America",
        i1_link: "https://indexone.io/index/5cd2eb5a-44f9-42e8-9c33-b040e3d9767f-0/overview",
        returns: [
            { label: "2022", value: "-33%" },
            { label: "2023", value: "51%" },
            { label: "2024", value: "12%" },
            { label: "2025", value: "12%" },
            { label: "YTD", value: "-6%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "DAILY" },
            { label: "HOLDINGS", value: "2" },
            { label: "DIVIDEND YIELD", value: "n/a" },
            { label: "NASDAQ", value: "ETHA (US)" },
            { label: "NASDAQ", value: "SHV (US)" }
        ]
    },
    {
        title: "USA Large Cap Size",
        description: "This strategy provides Size exposure to US large capitalisation stocks by mitigating the cognitive errors inherent within the market capitalisation distribution.",
        goLiveDate: "June 30, 2023",
        geography: "North America",
        i1_link: "https://indexone.io/index/e9ca4604-6f31-4041-bb6d-66774977bd76-0/overview",
        returns: [
            { label: "2022", value: "-21%" },
            { label: "2023", value: "30%" },
            { label: "2024", value: "33%" },
            { label: "2025", value: "20%" },
            { label: "YTD", value: "0%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "50" },
            { label: "DIVIDEND YIELD", value: "1.74%" },
            { label: "NASDAQ", value: "GOOGL (US)" },
            { label: "NASDAQ", value: "NVDA (US)" },
            { label: "NASDAQ", value: "AAPL (US)" }
        ]
    },
    {
        title: "USA Large Cap Growth-Income Switch",
        description: "This strategy provides general US large-cap exposure by switching between our US large Cap Growth & US Income strategies using a Growth-Value switch-signal.",
        goLiveDate: "August 08, 2024",
        geography: "North America",
        i1_link: "https://indexone.io/index/10367c83-e450-4927-8055-ad13db9a2ca3-0/overview",
        returns: [
            { label: "2022", value: "20%" },
            { label: "2023", value: "44%" },
            { label: "2024", value: "18%" },
            { label: "2025", value: "10%" },
            { label: "YTD", value: "5%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "TRIGGER" },
            { label: "HOLDINGS", value: "10" },
            { label: "DIVIDEND YIELD", value: "5.76%" },
            { label: "NYSE", value: "SWK (US)" },
            { label: "NYSE", value: "BEN (US)" },
            { label: "NYSE", value: "CVX (US)" }
        ]
    },
    {
        title: "USA Large Cap Risk Hedge",
        description: "This strategy provides general US large-cap exposure by switching between our US large Cap Growth & US Income strategies using a Risk On-Off switch signal.",
        goLiveDate: "May 29, 2025",
        geography: "North America",
        i1_link: "https://indexone.io/index/d231b0e9-4917-4679-a1be-29d27c89c370-0/overview",
        returns: [
            { label: "2022", value: "-7%" },
            { label: "2023", value: "30%" },
            { label: "2024", value: "32%" },
            { label: "2025", value: "18%" },
            { label: "YTD", value: "3%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "TRIGGER" },
            { label: "HOLDINGS", value: "77" },
            { label: "DIVIDEND YIELD", value: "2.15%" },
            { label: "NASDAQ", value: "GOOGL (US)" },
            { label: "NASDAQ", value: "NVDA (US)" },
            { label: "NYSE", value: "BAC (US)" }
        ]
    },
    {
        title: "USA Large Cap Growth",
        description: "This strategy provides exposure to US large capitalisation growth stocks powered by our proprietary PDE model.",
        goLiveDate: "August 08, 2024",
        geography: "North America",
        i1_link: "https://indexone.io/index/79ea74b7-7b1a-416a-b6fc-880dc8c45c8a-0/overview",
        returns: [
            { label: "2022", value: "-11%" },
            { label: "2023", value: "39%" },
            { label: "2024", value: "35%" },
            { label: "2025", value: "29%" },
            { label: "YTD", value: "0%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "10" },
            { label: "DIVIDEND YIELD", value: "1.74%" },
            { label: "NASDAQ", value: "AVGO (US)" },
            { label: "NASDAQ", value: "GOOGL (US)" },
            { label: "NASDAQ", value: "BAC (US)" }
        ]
    },
    {
        title: "Developed Europe Large Cap Growth",
        description: "This strategy provides exposure to Developed Europe large capitalisation growth stocks powered by our proprietary PDE (Phase Detection Engine) model.",
        goLiveDate: "July 29, 2024",
        geography: "Europe",
        i1_link: "https://indexone.io/index/719818d5-b600-4e6e-a6ca-3e85eea343b6-0/overview",
        returns: [
            { label: "2022", value: "-6%" },
            { label: "2023", value: "15%" },
            { label: "2024", value: "13%" },
            { label: "2025", value: "44%" },
            { label: "YTD", value: "5%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "20" },
            { label: "DIVIDEND YIELD", value: "2.03%" },
            { label: "XETRA", value: "ENR (DE)" },
            { label: "XETRA", value: "RHM (DE)" },
            { label: "AEX", value: "ABN (NL)" }
        ]
    },
    {
        title: "All Country World xUS xEU Large Cap Growth",
        description: "This strategy provides exposure to Global large capitalisation growth stocks outside the US & Developed Europe powered by our proprietary PDE (Phase Detection Engine) model.",
        goLiveDate: "July 31, 2024",
        geography: "Global",
        i1_link: "https://indexone.io/index/b2d842f8-3161-484a-82b7-ad18d9ea3730-0/overview",
        returns: [
            { label: "2022", value: "-6%" },
            { label: "2023", value: "32%" },
            { label: "2024", value: "27%" },
            { label: "2025", value: "10%" },
            { label: "YTD", value: "19%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "20" },
            { label: "DIVIDEND YIELD", value: "1.18%" },
            { label: "TSX", value: "CG (CA)" },
            { label: "TSE", value: "6503 (JP)" },
            { label: "NASDAQ", value: "KC (CN)" }
        ]
    },
    {
        title: "Japan Large Cap Growth",
        description: "This strategy provides exposure to Japan large capitalisation growth stocks powered by our proprietary PDE model.",
        goLiveDate: "March 14, 2025",
        geography: "Asia-Pacific",
        i1_link: "https://indexone.io/index/11bdd8e3-16d6-462f-a3d4-d6c29eda5fce-0/overview",
        returns: [
            { label: "2022", value: "-7%" },
            { label: "2023", value: "31%" },
            { label: "2024", value: "27%" },
            { label: "2025", value: "18%" },
            { label: "YTD", value: "32%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "20" },
            { label: "DIVIDEND YIELD", value: "1.18%" },
            { label: "TSE", value: "5803 (JP)" },
            { label: "TSE", value: "8015 (JP)" },
            { label: "TSE", value: "1812 (JP)" }
        ]
    },
    {
        title: "USA Income",
        description: "This strategy provides exposure to US income stocks by combining consistent long-term dividend growth with high current dividend yield, and powered by our proprietary PDE model.",
        goLiveDate: "March 15, 2024",
        geography: "North America",
        i1_link: "https://indexone.io/index/6e64d4ba-7681-40d4-91f5-73e6f80d7778-0/overview",
        returns: [
            { label: "2022", value: "20%" },
            { label: "2023", value: "10%" },
            { label: "2024", value: "17%" },
            { label: "2025", value: "2%" },
            { label: "YTD", value: "14%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "10" },
            { label: "DIVIDEND YIELD", value: "5.76%" },
            { label: "NYSE", value: "SWK (US)" },
            { label: "NYSE", value: "BEN (US)" },
            { label: "NYSE", value: "CVX (US)" }
        ]
    },
    {
        title: "Developed Europe Income",
        description: "This strategy provides exposure to Developed Europe income stocks by combining consistent long-term dividend growth with high current dividend yield, and powered by our proprietary PDE model.",
        goLiveDate: "March 15, 2024",
        geography: "Europe",
        i1_link: "https://indexone.io/index/16eaec99-b947-47c6-a8c1-c33a47aba918-0/overview",
        returns: [
            { label: "2022", value: "-10%" },
            { label: "2023", value: "20%" },
            { label: "2024", value: "16%" },
            { label: "2025", value: "21%" },
            { label: "YTD", value: "5%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "20" },
            { label: "DIVIDEND YIELD", value: "5.76%" },
            { label: "XETRA", value: "DBK (DE)" },
            { label: "ENXT PA", value: "ENGI (FR)" },
            { label: "XETRA", value: "HEI (DE)" }
        ]
    },
    {
        title: "USA SMID Growth",
        description: "This strategy provides exposure to US small & mid capitalisation growth stocks powered by our proprietary PDE model.",
        goLiveDate: "November 23, 2023",
        geography: "North America",
        i1_link: "https://indexone.io/index/db68089d-5744-4c9f-b5ca-349eefadf97a-0/overview",
        returns: [
            { label: "2022", value: "-16%" },
            { label: "2023", value: "7%" },
            { label: "2024", value: "13%" },
            { label: "2025", value: "18%" },
            { label: "YTD", value: "15%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "25" },
            { label: "DIVIDEND YIELD", value: "2.21%" },
            { label: "NASDAQ", value: "NXT (US)" },
            { label: "NASDAQ", value: "DXPE (US)" },
            { label: "NYSE", value: "CDE (US)" }
        ]
    },
    {
        title: "USA Large Cap Low Volatility",
        description: "This strategy provides exposure to US large capitalisation low volatility stocks powered by our proprietary PDE model.",
        goLiveDate: "October 31, 2023",
        geography: "North America",
        i1_link: "https://indexone.io/index/7e2721ba-1bd2-4257-adff-baea99662ebe-0/overview",
        returns: [
            { label: "2022", value: "-11%" },
            { label: "2023", value: "7%" },
            { label: "2024", value: "17%" },
            { label: "2025", value: "12%" },
            { label: "YTD", value: "7%" }
        ],
        metrics: [
            { label: "REBALANCING", value: "QUARTERLY" },
            { label: "HOLDINGS", value: "50" },
            { label: "DIVIDEND YIELD", value: "2.15%" },
            { label: "NYSE", value: "BAC (US)" },
            { label: "NYSE", value: "WELL (US)" },
            { label: "NYSE", value: "USB (US)" }
        ]
    }
];


function createSlide(index) {
    let slide = React.createElement('div', { className: "card" },
        React.createElement('h4', { className: "card-title" }, index.title,
            React.createElement('span', { className: "card-i1-link" },
                React.createElement('a', { href: index.i1_link, target: "_blank" }, <FcLineChart />)
            )
        ),
        React.createElement('span', { className: "card-geography" }, index.geography),
        React.createElement('span', { className: "card-go-live" }, "Live: ", index.goLiveDate),
        React.createElement('div', { className: "returns-grid" },
            React.createElement('div', { className: "return" },
                React.createElement('div', { className: "return-value" }, index.returns[0].value),
                React.createElement('div', { className: "return-label" }, index.returns[0].label)
            ),
            React.createElement('div', { className: "return" },
                React.createElement('div', { className: "return-value" }, index.returns[1].value),
                React.createElement('div', { className: "return-label" }, index.returns[1].label)
            ),
            React.createElement('div', { className: "return" },
                React.createElement('div', { className: "return-value" }, index.returns[2].value),
                React.createElement('div', { className: "return-label" }, index.returns[2].label)
            ),
            React.createElement('div', { className: "return" },
                React.createElement('div', { className: "return-value" }, index.returns[3].value),
                React.createElement('div', { className: "return-label" }, index.returns[3].label)
            ),
            React.createElement('div', { className: "return" },
                React.createElement('div', { className: "return-value" }, index.returns[4].value),
                React.createElement('div', { className: "return-label" }, index.returns[4].label)
            )
        ),
        React.createElement('p', { className: "card-description" }, index.description),
        React.createElement('div', { className: "metrics-grid" },
            React.createElement('div', { className: "metric" },
                React.createElement('div', { className: "metric-value" }, index.metrics[0].value),
                React.createElement('div', { className: "metric-label" }, index.metrics[0].label)
            ),
            React.createElement('div', { className: "metric" },
                React.createElement('div', { className: "metric-value" }, index.metrics[1].value),
                React.createElement('div', { className: "metric-label" }, index.metrics[1].label)
            ),
            React.createElement('div', { className: "metric" },
                React.createElement('div', { className: "metric-value" }, index.metrics[2].value),
                React.createElement('div', { className: "metric-label" }, index.metrics[2].label)
            ),

            index.metrics.length > 3 ? React.createElement('div', { className: "metric" },
                React.createElement('div', { className: "metric-value" }, index.metrics[3].value),
                React.createElement('div', { className: "metric-label" }, index.metrics[3].label)
            ) : React.createElement('div'),
            index.metrics.length > 4 ? React.createElement('div', { className: "metric" },
                React.createElement('div', { className: "metric-value" }, index.metrics[4].value),
                React.createElement('div', { className: "metric-label" }, index.metrics[4].label)
            ) : React.createElement('div'),
            index.metrics.length > 5 ? React.createElement('div', { className: "metric" },
                React.createElement('div', { className: "metric-value" }, index.metrics[5].value),
                React.createElement('div', { className: "metric-label" }, index.metrics[5].label)
            ) : React.createElement('div')
        )
    );

    return slide;
}

function SwiperSlides() {
    return (
        <>
            <SwiperSlide>{createSlide(piIndexes[0])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[1])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[2])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[3])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[4])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[5])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[6])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[7])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[8])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[9])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[10])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[11])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[12])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[13])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[14])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[15])}</SwiperSlide>
            <SwiperSlide>{createSlide(piIndexes[16])}</SwiperSlide>
        </>
    );
}

function SwiperSlider() {
    const widthResolution = window.screen.width;
    const isNav = widthResolution > 768 ? true : false;
    return (
        <>
            <Swiper
                slidesPerView={1}
                spaceBetween={0}
                breakpoints={{
                    240: {
                        slidesPerView: 1,
                        spaceBetween: 0,
                        navigation: {
                            enabled: false,
                            visibility: false,
                        },
                    },
                    768: {
                        slidesPerView: 1.2,
                        spaceBetween: 90,
                        navigation: {
                            enabled: true,
                            visibility: true,
                        },
                    },
                }}
                centeredSlides={true}
                loop={true}
                pagination={{
                    type: 'fraction',
                }}
                navigation={isNav}
                modules={[Pagination, Navigation]}
                className="my-swiper-wrapper"
            >
                {SwiperSlides()}
            </Swiper >
        </>
    );
}

export default SwiperSlider;
